'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ArrowUpRight, Search } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { categoryLabel, filterArticles, type BlogSummary } from '@/lib/marketing-content';
import { formatDate } from '@/lib/utils';
import { blogClusters } from '@/lib/blog-clusters';

function ArticleCard({ article, featured = false }: { article: BlogSummary; featured?: boolean }) {
  return (
    <Link
      href={`/blogs/${article.slug}`}
      className={featured ? 'marketing-featured-article' : 'marketing-article-card'}>
      <div className="marketing-article-image">
        <Image
          src={article.coverImage || '/placeholder.svg'}
          alt={article.title}
          fill
          sizes={featured ? '(max-width: 700px) 100vw, 60vw' : '(max-width: 700px) 100vw, 33vw'}
          priority={featured}
        />
      </div>
      <div className="marketing-article-card-copy">
        <span className="marketing-category">{categoryLabel(article.category)}</span>
        <h2>{article.title}</h2>
        <p>{article.excerpt}</p>
        <div className="marketing-article-meta">
          <time dateTime={article.date}>{formatDate(article.date)}</time>
          <span>{article.readingTime}</span>
        </div>
        <span className="marketing-text-link">
          Read the story <ArrowUpRight size={17} aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
}

export default function BlogsIndexPage({ articles }: { articles: BlogSummary[] }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const categories = Array.from(new Set(articles.map(article => article.category)));
  const filtered = filterArticles(articles, query, category);
  const featured =
    !query.trim() && category === 'all' ? filtered.find(article => article.featured) || filtered[0] : undefined;
  const remaining = featured ? filtered.filter(article => article.slug !== featured.slug) : filtered;
  return (
    <div className="marketing-theme marketing-blog">
      <Navbar />
      <main className="marketing-container marketing-directory">
        <header className="marketing-directory-heading">
          <p className="marketing-eyebrow">THE PLOTSALOT JOURNAL</p>
          <h1>
            Better charts.
            <br />
            <span>Clearer stories.</span>
          </h1>
          <p>Practical guides for making sense of your data, and making it look the part.</p>
        </header>
        {!query.trim() && category === 'all' && (
          <section className="marketing-clusters" aria-labelledby="cluster-heading">
            <p className="marketing-eyebrow" id="cluster-heading">
              START HERE
            </p>
            <div className="marketing-cluster-grid">
              {blogClusters
                .filter(cluster => articles.some(article => article.slug === cluster.hub))
                .map((cluster, index) => (
                  <Link key={cluster.id} href={`/blogs/${cluster.hub}`} className="marketing-cluster-card">
                    <span className="marketing-cluster-number">0{index + 1}</span>
                    <h2>{cluster.title}</h2>
                    <p>{cluster.description}</p>
                    <span className="marketing-text-link">
                      Explore the guide <ArrowUpRight size={16} aria-hidden="true" />
                    </span>
                  </Link>
                ))}
            </div>
          </section>
        )}
        <div className="marketing-filter-bar">
          <div className="marketing-filter-tabs" aria-label="Article categories">
            {['all', ...categories].map(value => (
              <button key={value} type="button" aria-pressed={category === value} onClick={() => setCategory(value)}>
                {value === 'all' ? 'All articles' : categoryLabel(value)}
              </button>
            ))}
          </div>
          <label className="marketing-search">
            <Search size={17} aria-hidden="true" />
            <span className="sr-only">Search articles</span>
            <input
              type="search"
              placeholder="Search articles"
              value={query}
              onChange={event => setQuery(event.target.value)}
            />
          </label>
        </div>
        <p className="marketing-results-count" role="status">
          {filtered.length} {filtered.length === 1 ? 'article' : 'articles'}
        </p>
        {featured && <ArticleCard article={featured} featured />}
        <div className="marketing-article-grid">
          {remaining.map(article => (
            <ArticleCard key={article.slug} article={article} />
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="marketing-empty">
            <h2>No stories found.</h2>
            <p>Try another search or browse all articles.</p>
            <button
              type="button"
              className="marketing-button marketing-button-white"
              onClick={() => {
                setQuery('');
                setCategory('all');
              }}>
              Clear filters
            </button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
